import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Layout, Avatar, Dropdown, message, Modal, Form, Input } from 'antd';
import {
    HomeOutlined,
    DatabaseOutlined,
    ProfileOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
    LogoutOutlined,
    DownOutlined,
    KeyOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, changePassword, editName } from '../../api/admin';
import { usePermCodes } from '../../hooks/usePermission';
import { MENU_PATH_CODE_MAP } from '../../constants/menuResource';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

// 一级菜单：ICON + 名称（窄栏），hover 弹出 二级(标题) + 三级 浮窗
// 三级菜单 key 为路由 path，点击跳转；无 children 的一级菜单直接点击跳转
const menuGroups = [
    {
        key: 'home',
        icon: <HomeOutlined />,
        label: '平台首页',
        path: '/',
    },
    {
        key: 'config',
        icon: <SettingOutlined />,
        label: '基础配置',
        children: [
            {
                key: 'config-platform',
                label: '平台与规则',
                children: [
                    { key: '/config/platforms', label: '运营平台配置' },
                    { key: '/config/mac-rule', label: 'MAC规则配置' },
                    { key: '/config/notify', label: '邮箱告警通知' },
                ]
            },
            {
                key: 'config-device',
                label: '工厂管理',
                children: [
                    { key: '/config/factories', label: '工厂管理' },
                ]
            },
        ]
    },
    {
        key: 'serial',
        icon: <DatabaseOutlined />,
        label: '串码管理',
        children: [
            {
                key: 'serial-resource',
                label: '串码资源',
                children: [
                    { key: '/serial/list', label: '串码池' },
                    { key: '/serial/import-records', label: '导入记录' },
                    { key: '/serial/void-records', label: '作废记录' },
                    { key: '/serial/reset-records', label: '重置记录' },
                    { key: '/serial/unbind-records', label: '解绑记录' },
                ]
            },
            {
                key: 'serial-shipment',
                label: '发货&售后',
                children: [
                    { key: '/serial/shipment', label: '发货报盘' },
                    { key: '/serial/return', label: '退货管理' },
                ]
            },
        ]
    },
    {
        key: 'order',
        icon: <ProfileOutlined />,
        label: '生产工单',
        children: [
            {
                key: 'order-manage',
                label: '工单管理',
                children: [
                    { key: '/order/list', label: '工单管理' },
                    { key: '/auth/alerts', label: '异常告警' },
                ]
            },
        ]
    },
    {
        key: 'system',
        icon: <TeamOutlined />,
        label: '系统管理',
        children: [
            {
                key: 'system-account',
                label: '账户权限',
                children: [
                    { key: '/system/accounts', label: '账户管理' },
                    { key: '/system/roles', label: '角色管理' },
                ]
            },
        ]
    },
];

const PAGE_TITLE_MAP = (() => {
    const map = {};
    menuGroups.forEach((group) => {
        if (group.path) map[group.path] = group.label;
        (group.children || []).forEach((sub) => {
            (sub.children || []).forEach((item) => {
                map[item.key] = item.label;
            });
        });
    });
    map['/serial/import'] = '串码导入';
    map['/serial/import-old'] = '导入旧平台串码';
    map['/home/dashboard'] = '工作台';
    return map;
})();

const MainLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const pageTitle = PAGE_TITLE_MAP[location.pathname] || '';
    const [userInfo, setUserInfo] = useState(null);
    const [changePwdVisible, setChangePwdVisible] = useState(false);
    const [pwdForm] = Form.useForm();
    const [changeNameVisible, setChangeNameVisible] = useState(false);
    const [nameForm] = Form.useForm();
    const [activeMenu, setActiveMenu] = useState(null);
    const [popoverMenu, setPopoverMenu] = useState(null);
    const [popoverTop, setPopoverTop] = useState(0);
    const popoverRef = useRef(null);

    const permCodes = usePermCodes();

    // 按用户菜单权限过滤侧边栏（无 code 的入口始终可见）
    const filteredMenuGroups = useMemo(() => {
        const codes = permCodes;
        return menuGroups.map((group) => {
            if (group.path) {
                const code = MENU_PATH_CODE_MAP[group.path];
                return code && !codes.has(code) ? null : group;
            }
            const children = (group.children || []).map((sub) => ({
                ...sub,
                children: sub.children.filter((item) => {
                    const code = MENU_PATH_CODE_MAP[item.key];
                    return !code || codes.has(code);
                }),
            })).filter((sub) => sub.children.length > 0);
            if (children.length === 0) return null;
            return { ...group, children };
        }).filter(Boolean);
    }, [permCodes]);

    // 构建 路由path → 一级菜单key 的映射，用于自动选中当前页面所属一级菜单
    const pathToGroupMap = useMemo(() => {
        const map = {};
        menuGroups.forEach((group) => {
            if (group.path) map[group.path] = group.key;
            (group.children || []).forEach((sub) => {
                (sub.children || []).forEach((item) => {
                    map[item.key] = group.key;
                });
            });
        });
        return map;
    }, []);

    const selectedKey = useMemo(() => {
        const pathname = location.pathname;
        if (pathToGroupMap[pathname]) return pathToGroupMap[pathname];
        // 兜底：详情页等非菜单路由，按最长前缀匹配所属一级菜单
        let bestKey = null;
        let bestLen = 0;
        Object.keys(pathToGroupMap).forEach((p) => {
            if (pathname === p || pathname.startsWith(p + '/')) {
                if (p.length > bestLen) {
                    bestLen = p.length;
                    bestKey = pathToGroupMap[p];
                }
            }
        });
        return bestKey;
    }, [location.pathname, pathToGroupMap]);

    // 浮窗置顶展示：浮窗撑满侧栏高度，鼠标水平移入不经过其他菜单项
    const popoverGroup = filteredMenuGroups.find((g) => g.key === popoverMenu) || null;

    const handleGroupEnter = (key, top) => {
        setActiveMenu(key);
        setPopoverMenu(key);
        setPopoverTop(top);
    };
    const handleNavLeave = () => {
        setActiveMenu(null);
        setPopoverMenu(null);
    };

    // 浮窗渲染后，按内容高度将 top 收拢在视口内（不顶出屏幕）
    useLayoutEffect(() => {
        if (!popoverMenu) return;
        const el = popoverRef.current;
        if (!el) return;
        const height = el.offsetHeight;
        const maxTop = Math.max(8, window.innerHeight - height - 8);
        setPopoverTop((cur) => Math.min(Math.max(cur, 8), maxTop));
    }, [popoverMenu]);
    const handleMenuClick = (key) => {
        setActiveMenu(null);
        setPopoverMenu(null);
        navigate(key);
    };

    useEffect(() => {
        const infoStr = localStorage.getItem('user_info');
        if (infoStr) {
            try {
                setUserInfo(JSON.parse(infoStr));
            } catch { setUserInfo(null); }
        }
    }, []);

    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('user_token');
            const res = await logout({ user_token: token });
            if (res.code === 0) {
                // 成功处理
            } else {
                if (res.message) message.error(res.message);
            }
        } catch (error) {
            console.error('Logout error:', error);
            if (error.message) message.error(error.message);
        } finally {
            localStorage.removeItem('user_token');
            localStorage.removeItem('user_info');
            localStorage.removeItem('user_codes');
            localStorage.removeItem('user_hidden_fields');
            navigate('/login');
        }
    };

    const handleChangePwdClick = () => {
        setChangePwdVisible(true);
        pwdForm.resetFields();
    };

    const handleChangePwdSubmit = async () => {
        try {
            const values = await pwdForm.validateFields();
            const res = await changePassword({
                old_pass: values.old_pass,
                new_pass: values.new_pass,
                confirm_pass: values.confirm_pass
            });

            if (res.code === 0) {
                message.success('修改成功, 请重新登录');
                setChangePwdVisible(false);
                localStorage.removeItem('user_token');
                localStorage.removeItem('user_info');
                localStorage.removeItem('user_codes');
                localStorage.removeItem('user_hidden_fields');
                navigate('/login');
            } else {
                message.error(res.message || '修改失败');
            }
        } catch (error) {
            console.error('Change password failed:', error);
            if (error.errorFields) return;
            message.error(error.message || '修改失败');
        }
    };

    const handleChangeNameSubmit = async () => {
        try {
            const values = await nameForm.validateFields();
            const res = await editName({
                nick_name: values.nick_name
            });

            if (res.code === 0) {
                message.success('修改成功');
                setChangeNameVisible(false);
                const newUserInfo = { ...userInfo, nick_name: values.nick_name };
                setUserInfo(newUserInfo);
                localStorage.setItem('user_info', JSON.stringify(newUserInfo));
            } else {
                message.error(res.message || '修改失败');
            }
        } catch (error) {
            console.error('Change name failed:', error);
            if (error.errorFields) return;
            message.error(error.message || '修改失败');
        }
    };

    const userMenuItems = [
        {
            key: 'personalCenter',
            icon: <UserOutlined />,
            label: '个人信息',
            onClick: () => navigate('/personal-center'),
        },
        {
            key: 'changePwd',
            icon: <KeyOutlined />,
            label: '修改密码',
            onClick: handleChangePwdClick,
        },
        {
            key: 'divider',
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '退出登录',
            onClick: handleLogout,
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider width={90} className="site-layout-sider">
                <div className="logo-container">
                    <div className="logo-icon">ELF</div>
                </div>
                <nav className="side-nav" onMouseLeave={handleNavLeave}>
                    {filteredMenuGroups.map((group) => (
                        <div
                            key={group.key}
                            className={`side-nav-item${selectedKey === group.key ? ' active' : ''}${activeMenu === group.key ? ' hover' : ''}`}
                            onMouseEnter={(e) => handleGroupEnter(group.key, e.currentTarget.offsetTop)}
                            onClick={() => group.path && handleMenuClick(group.path)}
                        >
                            <span className="side-nav-icon">{group.icon}</span>
                            <span className="side-nav-label">{group.label}</span>
                        </div>
                    ))}

                    {/* hover 浮窗：置顶展示，内容锁定首次进入的菜单组 */}
                    {popoverGroup && popoverGroup.children && (
                        <div
                            ref={popoverRef}
                            className="side-nav-popover"
                            style={{ top: popoverTop }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="side-nav-pop-grid">
                                {popoverGroup.children.map((sub) => (
                                    <div key={sub.key} className="side-nav-pop-col">
                                        <div className="side-nav-pop-sub">{sub.label}</div>
                                        <div className="side-nav-pop-items">
                                            {sub.children.map((item) => (
                                                <div
                                                    key={item.key}
                                                    className={`side-nav-pop-item${location.pathname === item.key ? ' active' : ''}`}
                                                    onClick={() => handleMenuClick(item.key)}
                                                >
                                                    {item.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </nav>
            </Sider>
            <Layout className="site-layout">
                <Header className="site-header">
                    {/* Header Left Content */}
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
                            易莱孚生产管理后台
                        </span>
                    </div>

                    {/* Header Right Content - User Profile */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ cursor: 'pointer' }}>
                            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar style={{ backgroundColor: '#ff6a00', marginRight: 8 }} icon={<UserOutlined />} />
                                    <span style={{ color: '#333', marginRight: 8 }}>{userInfo?.nick_name || userInfo?.user_name || 'User'}</span>
                                    <DownOutlined style={{ fontSize: '12px', color: '#999' }} />
                                </div>
                            </Dropdown>
                        </div>
                    </div>
                </Header>
                <Content style={{ margin: '24px 0 0', overflow: 'initial' }}>
                    <div className="site-content">
                        {pageTitle && <div className="page-title">{pageTitle}</div>}
                        {children}
                    </div>
                </Content>
            </Layout>

            {/* 修改密码 Modal */}
            <Modal
                title="修改密码"
                open={changePwdVisible}
                onOk={handleChangePwdSubmit}
                onCancel={() => setChangePwdVisible(false)}
                okText="确认"
                cancelText="取消"
                centered
            >
                <Form form={pwdForm} layout="vertical">
                    <Form.Item
                        name="old_pass"
                        label="旧密码"
                        rules={[{ required: true, message: '请输入旧密码' }]}
                    >
                        <Input.Password placeholder="请输入旧密码" />
                    </Form.Item>
                    <Form.Item
                        name="new_pass"
                        label="新密码"
                        rules={[{ required: true, message: '请输入新密码' }]}
                    >
                        <Input.Password placeholder="请输入新密码" />
                    </Form.Item>
                    <Form.Item
                        name="confirm_pass"
                        label="确认密码"
                        dependencies={['new_pass']}
                        rules={[
                            { required: true, message: '请确认新密码' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('new_pass') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="请再次输入新密码" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 修改昵称 Modal */}
            <Modal
                title="修改昵称"
                open={changeNameVisible}
                onOk={handleChangeNameSubmit}
                onCancel={() => setChangeNameVisible(false)}
                okText="保存"
                cancelText="取消"
                centered
            >
                <Form form={nameForm} layout="vertical">
                    <Form.Item
                        name="nick_name"
                        label="新昵称"
                        rules={[{ required: true, message: '请输入新昵称' }]}
                    >
                        <Input placeholder="请输入新昵称" />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default MainLayout;
